import { createContext, useState } from 'react';
import { useQueries, useQueryClient, UseQueryOptions } from 'react-query';
import { useToastNotifications } from '@nbirl/nbi-common-ui-libs';
import { without as _without } from 'lodash';

import {
  useContextFallback,
  useErrorToast,
  useSecurePortalFetcher,
} from '../../../../shared/hooks';
import { CopyFolderStatusResponse } from '../models';
import { useCopyFolderMutation } from '../queries';

type CopyFolderProcess = {
  folderId: string;
  processId: string;
  newFolderName: string;
};

type CopyFolderContextType = {
  copyFolder: (folderId: string, newFolderName: string) => Promise<void>;
};

const CopyFolderContext = createContext<CopyFolderContextType | undefined>(
  undefined
);
CopyFolderContext.displayName = 'CopyFolderContext';

export const CopyFolderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [processes, setProcesses] = useState<CopyFolderProcess[]>([]);
  const fetcher = useSecurePortalFetcher();
  const copyFolderMutation = useCopyFolderMutation();
  const { addToast, removeToast } = useToastNotifications();
  const { showErrorToast } = useErrorToast();
  const queryClient = useQueryClient();

  const showCopyFolderErrorToast = (newFolderName: string) => {
    showErrorToast(`${newFolderName} could not be copied. Please try again.`);
  };

  const getQuery = (
    process: CopyFolderProcess
  ): UseQueryOptions<CopyFolderStatusResponse> => {
    const { processId, newFolderName } = process;

    const handleCopyError = () => {
      setProcesses(_without(processes, process));
      removeToast(processId);
      showCopyFolderErrorToast(newFolderName);
    };

    return {
      queryKey: ['copy-folder-status', processId],
      refetchInterval: 2000,
      queryFn: async () => {
        const { data } = await fetcher<CopyFolderStatusResponse>(
          `/v1/folders/locks/${processId}`
        ).json();

        return data;
      },
      onSuccess: ({ status }) => {
        switch (status) {
          case 'FINISHED':
            setProcesses(_without(processes, process));
            removeToast(processId);

            addToast({
              variant: 'success',
              text: `Copy of folder ${newFolderName} successfully created.`,
            });

            queryClient.invalidateQueries('folders-tree');
            queryClient.invalidateQueries('folder-content');
            queryClient.invalidateQueries('documents');
            break;
          case 'FAILED':
            handleCopyError();
            break;
        }
      },
      onError: handleCopyError,
    };
  };

  useQueries(processes.map((def) => getQuery(def)) as UseQueryOptions[]);

  const copyFolder = async (folderId: string, newFolderName: string) => {
    try {
      const { copyProcessId } = await copyFolderMutation.mutateAsync({
        folderId,
        newFolderName,
      });

      setProcesses((prevState) => [
        ...prevState,
        {
          folderId,
          processId: copyProcessId,
          newFolderName,
        },
      ]);

      addToast({
        id: copyProcessId,
        variant: 'info',
        text: `Creating copy of folder ${newFolderName}.`,
        autoRemove: false,
      });
    } catch (error) {
      showCopyFolderErrorToast(newFolderName);

      throw error;
    }
  };

  return (
    <CopyFolderContext.Provider
      value={{
        copyFolder,
      }}
    >
      {children}
    </CopyFolderContext.Provider>
  );
};

export const useCopyFolderContext = () => useContextFallback(CopyFolderContext);
